import { UpdateUploadFormData, UploadFormData } from "@/schema/upload.schema";
import {
  UploadDetailsResponse,
  UploadInvoiceValidationDetails,
  UploadList,
  SkippedDetail,
} from "@/types/uploadInvoice";
import { api } from "@/utils/axiosInterceptor";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "./use-toast";

export const useImport = () => {
  const { data: importRecords, isLoading } = useQuery({
    queryKey: ["importRecords"],
    queryFn: async () => {
      const response = await api.get<UploadList[]>("/uploads");
      return response.data;
    },
  });

  const uploadFileMutation = useMutation({
    mutationFn: async (formData: UploadFormData) => {
      const multipartFormData = new FormData();

      //append typeCode
      multipartFormData.append("typeCode", formData.typeCode);

      //append strictValidation (defaults to false for permissive mode)
      multipartFormData.append(
        "strictValidation",
        String(formData.strictValidation ?? false),
      );

      //append mappingProfileId
      if (formData.mappingProfileId) {
        multipartFormData.append("mappingProfileId", formData.mappingProfileId);
      }

      //append file
      if (formData.file && formData.file.length > 0) {
        multipartFormData.append("file", formData.file[0]);
      }

      //send as multipart/form-data
      return await api.post("/upload", multipartFormData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
  });

  const updateFileMutation = useMutation({
    mutationFn: async (data: {
      formData: UpdateUploadFormData;
      uploadId: string;
    }) => {
      const multipartFormData = new FormData();

      //append file
      if (data.formData.file && data.formData.file.length > 0) {
        multipartFormData.append("file", data.formData.file[0]);
      }

      //send as multipart/form-data
      return await api.put(`/upload/${data.uploadId}`, multipartFormData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
  });

  return {
    importRecords,
    isLoading,
    uploadFile: uploadFileMutation.mutateAsync,
    isUploading: uploadFileMutation.isPending,
    updateFile: updateFileMutation.mutateAsync,
    isUpdating: updateFileMutation.isPending,
  };
};

export const useRevertImport = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const revertMutation = useMutation({
    mutationFn: async (uploadId: string) => {
      return await api.post(`/upload/${uploadId}/revert`);
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["importRecords"] });
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      toast({
        title: "Import Reverted",
        description:
          response.data?.message ??
          "The invoices from this import have been deleted.",
        duration: 5000,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Revert Failed",
        description:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to revert this import.",
        variant: "destructive",
        duration: 10000,
      });
    },
  });

  return {
    revertImport: revertMutation.mutateAsync,
    isReverting: revertMutation.isPending,
  };
};

export const useImportDetails = (importId: string, invoiceId: string) => {
  const { data: importDetailsData, isLoading } = useQuery({
    queryKey: ["importDetails", importId],
    queryFn: async () => {
      const response = await api.get<UploadDetailsResponse>(
        `/upload/${importId}`,
      );
      return response.data;
    },
  });

  const { data: invoiceValidationDetails, isLoading: isLoadingValidation } =
    useQuery({
      queryKey: ["invoiceValidationDetails", invoiceId],
      queryFn: async () => {
        const response = await api.get<UploadInvoiceValidationDetails>(
          `/upload/invoice/validation/${invoiceId}`,
        );
        return response.data;
      },
      enabled: invoiceId !== "",
    });

  return {
    importDetails: importDetailsData?.invoices ?? [],
    skippedDetails: (importDetailsData?.skippedDetails ?? null) as
      | SkippedDetail[]
      | null,
    isLoading,
    invoiceValidationDetails,
    isLoadingValidation,
  };
};

export const useDownloadTemplate = () => {
  const { toast } = useToast();

  const downloadMutation = useMutation({
    mutationFn: async (type: string) => {
      const response = await api.get(`/templates/${type}`, {
        responseType: "blob",
      });
      return response;
    },
    onSuccess: (response, type) => {
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const contentDisposition = response.headers["content-disposition"];
      let fileName = `${type}_template.xlsx`;

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
        if (fileNameMatch?.[1]) {
          fileName = fileNameMatch[1];
        }
      }

      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    onError: (error: any) => {
      toast({
        title: "Download Failed",
        description:
          error.response?.data?.message || "Failed to download template",
        variant: "destructive",
      });
    },
  });

  return {
    downloadTemplate: downloadMutation.mutate,
    isDownloading: downloadMutation.isPending,
  };
};
